import React, { useState } from 'react';
import { useMachines } from '../context/MachineContext';
import { useWorkspace } from '../context/WorkspaceContext';
import Toast, { useToast } from '../components/Toast';
import './DeviceConnect.css';

const MQTT_ENDPOINT = 'a1b2c3d4e5f6g7.iot.us-east-1.amazonaws.com';
const TOPIC_FORMAT  = 'cf/{workspaceId}/{machineId}/telemetry';

const DeviceConnect: React.FC = () => {
  const { machines } = useMachines();
  const { workspace } = useWorkspace();
  const { toasts, push, remove } = useToast();

  const [selectedMachine, setSelectedMachine] = useState(machines[0]?.machineId || '');
  const [step, setStep] = useState<1 | 2 | 3>(1);

  const wsId    = workspace?.workspaceId || 'demo';
  const machine = machines.find(m => m.machineId === selectedMachine);
  const topic   = TOPIC_FORMAT.replace('{workspaceId}', wsId).replace('{machineId}', selectedMachine);

  // Mock generated credentials
  const deviceId    = `device_${selectedMachine}_${wsId}`.slice(0, 32);
  const clientToken = `cftoken_${btoa(deviceId).replace(/=/g, '').slice(0, 24)}`;

  const copy = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => push(`${label} copied to clipboard`, 'info'));
  };

  const samplePayload = JSON.stringify({
    deviceId,
    machineId: selectedMachine,
    timestamp: new Date().toISOString(),
    sensors: machine?.sensors.map(s => ({
      sensorId: s.sensorId,
      type:     s.type,
      value:    s.currentValue ?? ((s.minThreshold + s.maxThreshold) / 2),
      unit:     s.unit,
    })) || [],
  }, null, 2);

  return (
    <div className="device-connect-page">
      <Toast toasts={toasts} onRemove={remove} />

      <div className="page-header">
        <div>
          <h1>🔌 Connect Device</h1>
          <p className="page-subtitle">Register a physical device and get MQTT connection credentials</p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="steps-bar card">
        {[
          { n: 1, label: 'Select Machine' },
          { n: 2, label: 'Connection Info' },
          { n: 3, label: 'Test & Verify'  },
        ].map(s => (
          <div
            key={s.n}
            className={`step-item ${step === s.n ? 'active' : ''} ${step > s.n ? 'done' : ''}`}
            onClick={() => setStep(s.n as 1 | 2 | 3)}
          >
            <div className="step-num">{step > s.n ? '✓' : s.n}</div>
            <span>{s.label}</span>
          </div>
        ))}
      </div>

      {/* Step 1 — Select machine */}
      {step === 1 && (
        <div className="step-content card">
          <h2>Select the machine to connect</h2>
          <p className="step-desc">Choose which machine this physical device will report data for.</p>

          {machines.length === 0 ? (
            <div className="no-machines">
              ⚠️ No machines configured yet. Go to <strong>Machines</strong> page to add one first.
            </div>
          ) : (
            <div className="machine-select-grid">
              {machines.map(m => (
                <div
                  key={m.machineId}
                  className={`machine-select-card ${selectedMachine === m.machineId ? 'selected' : ''}`}
                  onClick={() => setSelectedMachine(m.machineId)}
                >
                  <div className="msc-name">{m.name}</div>
                  <div className="msc-meta">{m.type} · {m.location}</div>
                  <div className="msc-sensors">📡 {m.sensors.length} sensors</div>
                </div>
              ))}
            </div>
          )}

          <div className="step-actions">
            <button
              className="btn-primary"
              disabled={!selectedMachine}
              onClick={() => setStep(2)}
            >
              Continue →
            </button>
          </div>
        </div>
      )}

      {/* Step 2 — Connection info */}
      {step === 2 && (
        <div className="step-content card">
          <h2>Connection Credentials</h2>
          <p className="step-desc">Use these values to configure your device firmware or SDK.</p>

          <div className="cred-grid">
            <CredRow label="MQTT Endpoint"  value={MQTT_ENDPOINT}  onCopy={() => copy(MQTT_ENDPOINT, 'MQTT Endpoint')} />
            <CredRow label="MQTT Port"      value="8883 (TLS)"     />
            <CredRow label="Topic"          value={topic}          onCopy={() => copy(topic, 'Topic')} mono />
            <CredRow label="Device ID"      value={deviceId}       onCopy={() => copy(deviceId, 'Device ID')} mono />
            <CredRow label="Client Token"   value={clientToken}    onCopy={() => copy(clientToken, 'Client Token')} mono secret />
            <CredRow label="Workspace ID"   value={wsId}           onCopy={() => copy(wsId, 'Workspace ID')} mono />
          </div>

          <div className="info-box">
            <strong>ℹ️ AWS IoT Core Integration</strong>
            <p>
              When the backend is deployed, devices will authenticate via X.509 certificates issued by AWS IoT Core.
              The client token above is a temporary placeholder for local development.
            </p>
          </div>

          <div className="step-actions">
            <button className="btn-secondary" onClick={() => setStep(1)}>← Back</button>
            <button className="btn-primary"   onClick={() => setStep(3)}>View Payload Format →</button>
          </div>
        </div>
      )}

      {/* Step 3 — Payload format */}
      {step === 3 && (
        <div className="step-content card">
          <h2>Expected Payload Format</h2>
          <p className="step-desc">
            Your device should publish JSON to the topic above at the configured sensor sampling intervals.
          </p>

          <div className="payload-header">
            <span>JSON Payload</span>
            <button className="copy-btn" onClick={() => copy(samplePayload, 'Payload')}>📋 Copy</button>
          </div>
          <pre className="payload-block">{samplePayload}</pre>

          <div className="sdk-section">
            <h3>Quick Start — Node.js</h3>
            <pre className="payload-block">{`const mqtt = require('mqtt');
const client = mqtt.connect('mqtts://${MQTT_ENDPOINT}', {
  clientId: '${deviceId}',
  username: '${clientToken}',
  port: 8883,
});

client.on('connect', () => {
  setInterval(() => {
    client.publish('${topic}', JSON.stringify({
      deviceId: '${deviceId}',
      machineId: '${selectedMachine}',
      timestamp: new Date().toISOString(),
      sensors: [ /* your sensor readings */ ],
    }));
  }, 5000);
});`}</pre>
          </div>

          <div className="step-actions">
            <button className="btn-secondary" onClick={() => setStep(2)}>← Back</button>
            <button className="btn-primary" onClick={() => push('Device registered! Waiting for first message…', 'success')}>
              ✅ Mark as Registered
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const CredRow: React.FC<{
  label:   string;
  value:   string;
  onCopy?: () => void;
  mono?:   boolean;
  secret?: boolean;
}> = ({ label, value, onCopy, mono, secret }) => {
  const [show, setShow] = useState(!secret);
  return (
    <div className="cred-row">
      <span className="cred-label">{label}</span>
      <span className={`cred-value ${mono ? 'mono' : ''}`}>
        {secret && !show ? '••••••••••••••••••••••••' : value}
      </span>
      <div className="cred-actions">
        {secret && (
          <button className="icon-btn" onClick={() => setShow(p => !p)}>{show ? '🙈' : '👁️'}</button>
        )}
        {onCopy && <button className="icon-btn" onClick={onCopy}>📋</button>}
      </div>
    </div>
  );
};

export default DeviceConnect;
